import Debug from 'debug';

import { OfficeError, RestError } from '../errors';
import { retry } from '../../lib/retry';
import { getMailbox, isApiSupported, itemIsAppointmentCompose, itemIsMessageCompose } from './office';
import {
  RestEndpoint,
  getGraphAccessToken,
  getOutlookDefaultFetchParams,
  getOutlookItemChangeKey,
  getOutlookURL,
  monitorOutlookItemForServerUpdate,
  getOutlookItemParentFolder,
} from './outlook-rest-api';
import type {
  ComposeBase,
  ComposeAppointment,
  ComposeMessage,
  OutlookEmailAddress,
  OutlookRecipientSet,
  ReadBase,
} from './types';

/** The property name used in the item's custom property store. */
const BIXEL_PROPERTY_ID = 'BB-Bixel-Id';

/**
 * Wraps a ComposeBase and provides asynchronous wrappers and translations around
 * common functions
 */
export class OutlookCompose {
  protected lastChangeKey: string | null = null;
  protected debug = Debug('o365:OutlookCompose');
  protected searchableIdentifier: string | null = null;

  constructor(protected item: ComposeBase) {}

  /** Checks whether the item's body is in a format we can transform. */
  canTransform(): Promise<boolean> {
    if ('sender' in this.item) {
      this.debug('canTransform(): false -- "sender" is not in item');
      return Promise.resolve(false);
    }

    return new Promise((resolve, reject) => {
      this.item.body.getTypeAsync({}, (result) => {
        this.debug('canTransform(): getTypeAsync', result);
        if (result.status === Office.AsyncResultStatus.Failed) {
          return reject(new OfficeError(result.error));
        }

        resolve(result.value === Office.CoercionType.Html);
      });
    });
  }

  /** Gets the Bixel ID from the custom property store of the item, if available. */
  getBixelId(): Promise<string | null> {
    return new Promise((resolve, reject) => {
      this.item.loadCustomPropertiesAsync((result) => {
        this.debug('getBixelId()', result);
        if (result.status === Office.AsyncResultStatus.Failed) {
          return reject(new OfficeError(result.error));
        }

        const properties = result.value;
        const id = properties.get(BIXEL_PROPERTY_ID);
        resolve(id ? `${id}` : null);
      });
    });
  }

  /**
   * Remove the bixel ID from custom properties.
   */
  removeBixelId(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.item.loadCustomPropertiesAsync((result) => {
        this.debug('setBixelId()', result);
        if (result.status === Office.AsyncResultStatus.Failed) {
          return reject(new OfficeError(result.error));
        }

        const properties = result.value;
        properties.remove(BIXEL_PROPERTY_ID);
        properties.saveAsync((saveResult) => {
          if (saveResult.status === Office.AsyncResultStatus.Failed) {
            return reject(new OfficeError(saveResult.error));
          }

          resolve();
        });
      });
    });
  }

  /** Sets the Bixel ID in the item's custom property store. */
  setBixelId(id: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.item.loadCustomPropertiesAsync((result) => {
        this.debug('setBixelId()', result);
        if (result.status === Office.AsyncResultStatus.Failed) {
          return reject(new OfficeError(result.error));
        }

        const properties = result.value;
        if (properties.get(BIXEL_PROPERTY_ID) === id) {
          this.debug('setBixelId(): identical id already set');
          return resolve();
        }

        properties.set(BIXEL_PROPERTY_ID, id);
        properties.saveAsync((saveResult) => {
          if (saveResult.status === Office.AsyncResultStatus.Failed) {
            return reject(new OfficeError(saveResult.error));
          }

          resolve();
        });
      });
    });
  }

  /** Gets the item's content of the body as HTML. */
  getBody(): Promise<string> {
    return new Promise((resolve, reject) => {
      this.item.body.getAsync(Office.CoercionType.Html, {}, (result) => {
        this.debug('getBody()', result);
        if (result.status === Office.AsyncResultStatus.Failed) {
          return reject(new OfficeError(result.error));
        }

        resolve(result.value);
      });
    });
  }

  /**
   * Replaces the item's body content as HTML with the provided value.
   * @arg body The value to set the body to.
   */
  setBody(body: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.item.body.setAsync(body, { coercionType: Office.CoercionType.Html }, (result) => {
        this.debug('setBody()', result);
        if (result.status === Office.AsyncResultStatus.Failed) {
          return reject(new OfficeError(result.error));
        }

        resolve();
      });
    });
  }

  /** Gets the current change key for the item, if available. */
  async getCurrentChangeKey(): Promise<string | null> {
    this.debug('getCurrentChangeKey()');
    const [itemId, token] = await Promise.all([
      this.getItemId(),
      retry(async () => ({ status: 'success', result: await getGraphAccessToken() }), {
        canRetryOnError: (error) => {
          // see if we can try again shortly...
          return error instanceof OfficeError && error.name === 'APICallFailedDueToItemChange';
        },
        onRetry: () => this.debug('getCurrentChangeKey() retrying getOutlookApiBearerToken()'),
      }),
    ]);

    if (token === null) {
      this.debug('getCurrentChangeKey(): Could not get token', token);
      throw new Error('Could not get Outlook REST API Token');
    }

    let changeKey: string | null = null;
    if (itemId) {
      changeKey = await getOutlookItemChangeKey({ itemId, token });
    }

    return changeKey;
  }

  /**
   * Gets the current item's id, if it's available. Null will be returned if the item has not
   * yet been saved.
   */
  getItemId(): Promise<string | null> {
    if (!isApiSupported('Mailbox', '1.8')) {
      this.debug('getItemId(): returning null id since API 1.8 is not supported.');
      return Promise.resolve(null);
    }

    return new Promise((resolve, reject) => {
      this.item.getItemIdAsync({}, (result) => {
        this.debug('getItemId()', result);
        if (result.status === Office.AsyncResultStatus.Failed) {
          if (result.error.name === 'ItemNotSaved') {
            return resolve(null);
          }

          return reject(new OfficeError(result.error));
        }

        const mailbox = getMailbox();
        const itemId = mailbox.convertToRestId(result.value, Office.MailboxEnums.RestVersion.v2_0);
        resolve(itemId);
      });
    });
  }

  /** Gets all of the recipients of the item (to, cc, bcc or required and optional attendees) as a set. */
  async getRecipients(): Promise<OutlookRecipientSet> {
    if (itemIsMessageCompose(this.item)) {
      const to = await getRecipientList('to', this.item);
      const cc = await getRecipientList('cc', this.item);
      const bcc = await getRecipientList('bcc', this.item);

      return { to, cc, bcc };
    }

    if (itemIsAppointmentCompose(this.item)) {
      const to = await getRecipientList('requiredAttendees', this.item);
      const cc = await getRecipientList('optionalAttendees', this.item);

      return { to, cc, bcc: [] };
    }

    return { to: [], cc: [], bcc: [] };
  }

  /** Gets the subject of the item. */
  getSubject(): Promise<string> {
    return new Promise((resolve, reject) => {
      this.item.subject.getAsync({}, (result) => {
        this.debug('getSubject()', result);
        if (result.status === Office.AsyncResultStatus.Failed) {
          return reject(new OfficeError(result.error));
        }

        resolve(result.value);
      });
    });
  }

  /**
   * Saves any changes to the item and persists them to storage.
   * @returns Returns the item's generated ID suitable for use in REST calls.
   */
  save(): Promise<string> {
    return new Promise((resolve, reject) => {
      this.item.saveAsync({}, (result) => {
        this.debug('save()', result);
        if (result.status === Office.AsyncResultStatus.Failed) {
          return reject(new OfficeError(result.error));
        }

        const mailbox = getMailbox();
        const itemId = mailbox.convertToRestId(result.value, Office.MailboxEnums.RestVersion.v2_0);
        resolve(itemId);
      });
    });
  }

  /**
   * Saves the item if the item is a draft (has a prior change key).
   * @throws
   */
  async saveIfDraft(): Promise<boolean> {
    this.debug('saveIfDraft()');
    const changeKey = await this.getCurrentChangeKey();
    if (changeKey) {
      this.lastChangeKey = changeKey;
      await this.save();
      return true;
    }

    return false;
  }

  /**
   * Saves the item and waits for the item update to propagate to the server via Outlook
   * REST api calls.
   * @arg signal Optionally provide an abort signal to control the maximum time this method
   * will take to execute.
   * @throws
   */
  async send(signal?: AbortSignal): Promise<void> {
    // try to get the token with retries (getOutlookApiBearerToken() throws on error)
    // so the retry functionality will retry only if we get an expected error in canRetryOnError
    const token = await retry(async () => ({ status: 'success', result: await getGraphAccessToken() }), {
      canRetryOnError: (error) => {
        // see if this will clear up if we retry shortly...
        return error instanceof OfficeError && error.name === 'APICallFailedDueToItemChange';
      },
      onComplete: (retries) => this.debug('send(): getOutlookApiBearerToken retries', retries),
      onRetry: (retryNumber) => this.debug(`send(): retrying getOutlookApiBearerToken() #${retryNumber}`),
    });
    const changeKey: string | null = this.lastChangeKey;
    const bodyId = this.searchableIdentifier;
    const itemId = await this.save();
    this.debug('send(): itemId & changeKey', itemId, changeKey);

    await monitorOutlookItemForServerUpdate({ bodyId, itemId, changeKey, token }, signal);

    const { method, url } = getOutlookURL(itemId, RestEndpoint.SendEmail);

    const params: RequestInit = {
      ...getOutlookDefaultFetchParams(token),
      method,
      signal,
    };

    const response = await fetch(url.toString(), params);
    if (!response.ok) {
      this.debug('end(): bad response from send endpoint', response);
      throw new RestError('Unexpected response when sending message.', response);
    }
  }

  async sendTokenless(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.item.sendAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          return reject(new OfficeError(result.error));
        }
        resolve();
      });
    });
  }

  /**
   * Sets the searchable identifier, used for tracking body updates when sending.
   * @arg id The searchable identifier to use when searching the body for an update.
   */
  setSearchableIdentifier(id: string | null) {
    this.searchableIdentifier = id;
  }
}

export class OutlookRead {
  constructor(protected item: ReadBase) {}

  /**
   * @throws
   */
  async getCurrentParentFolder(): Promise<string | null> {
    const token = await getGraphAccessToken();

    const mailbox = getMailbox();
    const itemId = mailbox.convertToRestId(this.item.itemId, Office.MailboxEnums.RestVersion.v2_0);
    let parentFolderId: string | null = null;
    if (itemId) {
      parentFolderId = await getOutlookItemParentFolder({ itemId, token });
    }

    return parentFolderId;
  }
}

/** Gets users folders */
export async function getSentAndInboxFolders() {
  const debug = Debug('o365:getSentAndInboxFolders');
  const token = await getGraphAccessToken();
  const { method, url } = getOutlookURL(null, RestEndpoint.MailFolders);

  url.searchParams.append('$filter', "DisplayName eq 'Sent Items' or DisplayName eq 'Inbox' ");

  const params: RequestInit = {
    ...getOutlookDefaultFetchParams(token),
    method,
  };
  const response = await fetch(url.toString(), params);

  if (!response.ok) {
    debug('getSentAndInboxFolders(): bad response from mailFolders endpoint', response);
    return;
  }
  const result = await response.json();

  const mailFolders = result.value.map((item: { Id: string; DisplayName: string }) => {
    return { id: item.Id, displayName: item.DisplayName };
  });

  localStorage.setItem('mailFolders', JSON.stringify(mailFolders));
}

/**
 * Gets the recipients from the given set (to, cc, bcc or required and optional attendees ) and converts them
 * to {@see OutlookEmailAddress} entries.
 * @arg type The type of list to query (must match key on appropraite type)
 * @arg item The item to query the lists from
 * @returns An array of converted email entries or an empty list if there were none.
 */
function getRecipientList(type: 'to' | 'cc' | 'bcc', item: ComposeMessage): Promise<OutlookEmailAddress[]>;
function getRecipientList(
  type: 'requiredAttendees' | 'optionalAttendees',
  item: ComposeAppointment,
): Promise<OutlookEmailAddress[]>;
function getRecipientList(
  type: string,
  item: ComposeMessage | ComposeAppointment,
): Promise<OutlookEmailAddress[]> {
  let list: Office.Recipients | null = null;
  if (itemIsMessageCompose(item) && ['to', 'cc', 'bcc'].includes(type)) {
    list = item[type as keyof Pick<ComposeMessage, 'to' | 'cc' | 'bcc'>];
  } else if (itemIsAppointmentCompose(item) && ['requiredAttendees', 'optionalAttendees'].includes(type)) {
    list = item[type as keyof Pick<ComposeAppointment, 'optionalAttendees' | 'requiredAttendees'>];
  }

  return new Promise((resolve, reject) => {
    // typescript won't let us put this outside for whatever reason
    if (!list) {
      reject(new Error('Incoming type was not message or appointment compose'));
      return;
    }

    list.getAsync({}, (result) => {
      Debug('o365:getRecipientList()')(`result for ${type}`, result);
      if (result.status === Office.AsyncResultStatus.Failed) {
        return reject(new OfficeError(result.error));
      }

      resolve(result.value.map((address) => ({ name: address.displayName, email: address.emailAddress })));
    });
  });
}
